// @ts-nocheck
import { createContext, useEffect, useState } from 'react';
import type { Policy } from 'Backend/policies/types';

import Config from 'Config';
import { getCurrentCompanyId, signoutWhenExpiredToken } from 'Lib/Utils';
import {
    createQuote,
    getWithoutOverride,
    httpDelete,
    patchWithoutOverride,
    postWithoutOverride,
    putWithoutOverride,
    requestWithoutOverride,
} from 'Redux/api';
import { ResponseSuccess } from './users/getAttachedCompanies';
import { PrismItem } from 'Components/UI_Library/Prism/types';
import type { CreditCard } from '@monorepo/paytron-receivables/src/app/creditcards/creditCards';
import { setAttachedCompanies } from './users/utils/attachedCompanies';
import { v0 } from './beneficiaryApi';

export type BackendAccessContextType = typeof API &
    ReturnType<typeof xeroApiMethods> & {
        setTrackingCategoryData: React.Dispatch<React.SetStateAction<unknown>>;
        prism: { [key: string]: PrismItem };
        isPrismLoading: boolean;
    };
// Important note here is that we wrap the entire app in the backend context so everything will have all the values and nothing will use the initial values.
export const BackendAccessContext = createContext({}) satisfies React.Context<
    Partial<BackendAccessContextType>
> as unknown as React.Context<BackendAccessContextType>;

export const API = {
    getApprovalPreferences() {},

    getTransactionsFromPaymentId(params) {
        const url = `${Config.api}/transactions/v1?paymentId=${params.PaymentId}`;
        return getWithoutOverride(url, null, null, true, true);
    },

    // ================================================= paytron-transactions =================================================
    getTransactions(params) {
        let url = `${Config.transactions_api}/transactions?`;
        Object.keys(params).forEach((key) => {
            url += `&${key}=${params[key]}`;
        });
        return getWithoutOverride(url, {}, null, true, true);
    },

    generateTransactionsFile(params) {
        let url = `${Config.transactions_api}/export?`;
        Object.keys(params).forEach((key) => {
            url += `&${key}=${
                Array.isArray(params[key])
                    ? encodeURIComponent(
                          params[key].map((i, index) => `${i}${params[key].length - 1 === index ? '' : '+'}`).join('')
                      )
                    : params[key]
            }`;
        });
        return getWithoutOverride(url, {}, null, true, true);
    },

    fetchTransactionsFile(params) {
        let url = `${Config.transactions_api}/file?`;
        Object.keys(params).forEach((key) => {
            url += `&${key}=${params[key]}`;
        });
        return getWithoutOverride(url, {}, null, true, true);
    },

    /** get balance statement pdf
     *
     * @param { startDate: string, endDate: string, currency: string } params
     */
    getBalanceStatement(params) {
        return getWithoutOverride(`${Config.transactions_api}/balance-statements`, params, null, true, true);
    },

    getPrismFeatures() {
        return requestWithoutOverride({
            url: Config.prism_api,
            payload: null,
            useToken: false,
        });
    },

    createUploadLink(companyId, beneficialOwnerId) {
        return postWithoutOverride(
            `${Config.kyc_private_api}/kyc?BeneficialOwnerId=${beneficialOwnerId}&CompanyId=${companyId}`,
            null,
            false,
            true,
            true
        );
    },

    uploadDocsPublic(params) {
        return postWithoutOverride(`${Config.kyc_public_api}/kyc`, params);
    },

    // ================================================= paytron-notes =================================================
    getNotesFromObjectId(resourceId, loadMore) {
        return getWithoutOverride(
            `${Config.notes_api}/?ParentId=${resourceId}${loadMore ? `&Start=${loadMore}` : ''}`,
            {},
            null,
            true,
            true
        );
    },

    addNoteToResource(params) {
        return postWithoutOverride(Config.notes_api, params, null, true, true);
    },

    removeNoteFromResource(id) {
        return httpDelete(`${Config.notes_api}/${id}`, {}, null, true, true);
    },

    updateNoteFromResource(id, params) {
        return putWithoutOverride(`${Config.notes_api}/${id}`, params, null, true, true);
    },
    // =================================================================================================================

    movePaymentBackToDrafts(paymentId) {
        return patchWithoutOverride(`${Config.api}/payments/v2/${paymentId}`, { status: 'DRAFT' }, null, true, true);
    },

    copyPayment(paymentId) {
        return postWithoutOverride(
            `${Config.payments_api}/payments/${paymentId}/copy?companyId=${getCurrentCompanyId()}`,
            null,
            null,
            true,
            true
        );
    },

    parseAbaFile(params) {
        return postWithoutOverride(`${Config.payment_api}/batch/aba`, params, null, true, true);
    },

    parseCsvFile(params) {
        return postWithoutOverride(`${Config.payment_api}/batch/csv`, params, null, true, true);
    },
    uploadInternational(params) {
        return postWithoutOverride(`${Config.bulk_payments_api}/`, params, null, true);
    },

    getNonTenantUsers() {
        return getWithoutOverride(`${Config.subscriptions_api}/non-tenant-users`, {}, null, true, true);
    },

    // ================================================= paytron-payroll =================================================
    connectClient(params) {
        return postWithoutOverride(`${Config.payroll_api}/payrolls/register`, params, null, true, true);
    },

    getClientName(name) {
        return getWithoutOverride(`${Config.payroll_api}/api-client/${name}`, {}, null, true, true);
    },

    submitPayrollFile(params) {
        return postWithoutOverride(`${Config.payroll_api}/payrolls`, params, null, true, true);
    },

    schedulePayroll(payrollId) {
        return postWithoutOverride(`${Config.payroll_api}/payrolls/schedule/${payrollId}`, {}, null, true, true);
    },

    validatePayroll(params) {
        return postWithoutOverride(`${Config.payroll_api}/payrolls/validate`, params, null, true, true);
    },

    getPayroll(payrollId) {
        return getWithoutOverride(`${Config.payroll_api}/payrolls/${payrollId}`, {}, null, true, true);
    },
    // ========================================================================================================================

    async deleteBeneficiary(id) {
        try {
            return await v0.deleteBeneficiary(id);
        } catch (err) {
            await signoutWhenExpiredToken();
            return { success: false };
        }
    },

    /** @deprecated use {@link useGetAttachedCompanies} instead */
    async getAttachedCompanies(userStatus?: string): Promise<
        | {
              success: true;
              data: ResponseSuccess['data'];
              next: string;
          }
        | { success: false; data?: never; next?: never }
    > {
        const url = new URL(`${Config.users_v1_api}/companies`);

        if (userStatus) {
            url.searchParams.append('status', userStatus);
        }
        try {
            const result = await getWithoutOverride<ResponseSuccess>(url, {}, null, true, true);
            !userStatus && setAttachedCompanies(result?.data?.data ?? []);
            return {
                success: true,
                data: result?.data?.data || [],
                next: result?.data?.next,
            };
        } catch (err: any) {
            console.error(err);
            return {
                success: false,
                data: err?.response?.data || '',
            };
        }
    },

    createUserSignUp(params) {
        return postWithoutOverride(`${Config.users_api}/signup`, params);
    },

    checkEmail(params) {
        return requestWithoutOverride({
            url: `${Config.users_api}/source`,
            queryParams: params,
            payload: null,
            useToken: false,
        });
    },

    getDirectDebits(companyId, paymentId) {
        return getWithoutOverride(
            `${Config.direct_debits_api}?companyId=${companyId}${paymentId ? `&paymentId=${paymentId}` : ''}`,
            null,
            null,
            true,
            false
        );
    },

    getDirectDebitFee() {
        return getWithoutOverride(`${Config.direct_debits_api}/fees`, {}, null, true, false);
    },

    /**
     * Create new direct debit
     *
     * @param {TotalAmount: number, CompanyId: string, ClearingDate?: string} params
     */
    createDirectDebit(params) {
        return postWithoutOverride(`${Config.direct_debits_api}/${getCurrentCompanyId()}`, params, null, true, false);
    },

    deleteDirectDebit(id) {
        return httpDelete(`${Config.direct_debits_api}/${getCurrentCompanyId()}/${id}`, {}, null, true, false);
    },

    updateDirectDebit(id, params) {
        return putWithoutOverride(
            `${Config.direct_debits_api}/${getCurrentCompanyId()}/${id}`,
            params,
            null,
            true,
            false
        );
    },

    /**
     * @param {pageKey?: object, limit?: number} params
     * @returns {pageKey?: string, directDebits: Array<DirectDebitItem>} - List of direct debits
     */
    listDirectDebits(params) {
        return getWithoutOverride(`${Config.direct_debits_api}/${getCurrentCompanyId()}`, params, null, true, false);
    },

    /**
     * Get earliest direct debit clearing date
     *
     * @returns earliestClearingDate: string
     */
    getEarliestClearingDate() {
        return getWithoutOverride(`${Config.direct_debits_api}/earliest`, {}, null, true, false);
    },

    /**
     * Check if payment has direct debit
     *
     * @param {paymentId: string} paymentId
     * @returns Array<DirectDebitPayment>
     */
    getDirectDebitPayment(paymentId) {
        return getWithoutOverride(
            `${Config.direct_debits_api}/${getCurrentCompanyId()}/${paymentId}`,
            {},
            null,
            true,
            false
        );
    },

    cancelScheduledDirectDebit(id) {
        return httpDelete(`${Config.direct_debits_api}/${getCurrentCompanyId()}/${id}`, {}, null, true, false);
    },

    verifyDirectDebitRequest(amount) {
        return postWithoutOverride(
            `${Config.direct_debits_api}/${getCurrentCompanyId()}/verify`,
            { amount },
            null,
            true,
            false
        );
    },

    getCompanyReferralInfo(code) {
        return getWithoutOverride(`${Config.referrals_api}/${code}`, null, false);
    },

    getCompanyRefers() {
        const CompanyId = getCurrentCompanyId();
        return getWithoutOverride(`${Config.referrals_api}/${CompanyId}/refers`, {}, null, true);
    },
    getCardTransactions(cardId, nextPage?: unknown) {
        return getWithoutOverride(
            `${Config.cards_api}/card-transactions?${cardId ? `cardId=${cardId}&` : ''}${
                nextPage ? `start=${nextPage}` : ''
            }`,
            {},
            null,
            true,
            true
        );
    },
    getCardTransaction(id, cardId) {
        return getWithoutOverride(`${Config.cards_api}/card-transactions/${id}?cardId=${cardId}`, {}, null, true, true);
    },

    updateCardTransactions({ transactions }) {
        const params = {
            transactions,
        };
        return putWithoutOverride(`${Config.cards_api}/card-transactions`, params, null, true, true);
    },

    getFundByCards() {
        return getWithoutOverride(
            `${Config.fund_by_card_api}?companyId=${getCurrentCompanyId()}`,
            {},
            null,
            true,
            false
        );
    },

    createFundByCard(params: CreateCreditCard): Promise<{ data?: { creditCard?: CreditCard } }> {
        return postWithoutOverride<{ data?: { creditCard?: CreditCard } }>(
            `${Config.fund_by_card_api}?companyId=${getCurrentCompanyId()}`,
            params,
            null,
            true,
            false
        );
    },

    deleteFundByCard(cardId) {
        const params = {
            companyId: getCurrentCompanyId(),
            cardId,
        };
        return httpDelete(`${Config.fund_by_card_api}`, params, null, true, false);
    },

    chargeFundByCard(params): Promise<{ data?: { transaction?: { RedirectUrl?: string }[] } }> {
        return postWithoutOverride<{ data?: { transaction?: { RedirectUrl?: string }[] } }>(
            `${Config.fund_by_card_api}/charge`,
            params,
            null,
            true,
            false
        );
    },

    finaliseFundByCard(params) {
        return postWithoutOverride(`${Config.fund_by_card_api}/charge/finalise`, params, null, true, false);
    },

    getFundByCardChargeFees(amount, currency, cardScheme, issuingCountry) {
        return getWithoutOverride(
            `${Config.fund_by_card_api}/fees?amount=${amount}&currency=${currency}&card=${cardScheme}&issuingCountry=${issuingCountry}`,
            null,
            true,
            true,
            false
        );
    },
    getCardTransactionsTimeSeries(params) {
        return getWithoutOverride(
            `${Config.cards_api}/analytics/card-transactions-timeseries`,
            params,
            null,
            true,
            true
        );
    },

    getCardTransactionsCounts(params) {
        return getWithoutOverride(`${Config.cards_api}/analytics/card-transactions-counts`, params, null, true, true);
    },
    getCardsUsage(params) {
        return getWithoutOverride(`${Config.cards_api}/analytics/cards-usage`, params, null, true, true);
    },
    getPaymentConfirmationPDF(paymentId) {
        return getWithoutOverride(
            `${Config.api}/payments/v2/${paymentId}/payment-confirmation`,
            null,
            null,
            true,
            true
        );
    },

    /**
     * @param { index: number, excludeReceipts: boolean, excludeExpenses: boolean, status: enum['DRAFT', 'SENDING', 'SENT'], transactionState: 'pending' | 'settled' = 'settled' } params
     */
    getExpenses(params) {
        return getWithoutOverride(`${Config.cards_api}/expenses`, params, null, true, true);
    },
    downloadExpenses(params) {
        return getWithoutOverride(`${Config.cards_api}/expenses/export`, params, null, true, true);
    },
    /**
     * @param { companyId: string, merchantName: string, merchantCategory: string, transactionDate: string, amount: string, currency: string } params
     */
    createExpense(params) {
        return postWithoutOverride<Partial<{ id: string }>>(`${Config.cards_api}/expenses`, params, null, true, true); // defensively typed response based on existing usage
    },
    /**
     * @param { expenses: ['transactionId', 'transactionId'] } params
     */
    deleteExpenses(params) {
        return httpDelete(`${Config.cards_api}/expenses`, params, null, true, true);
    },
    /** submit card transactions
     *
     * @param { transactions: ['transactionId', 'transactionId'] } params
     */
    submitCardTransactions(params) {
        return postWithoutOverride(`${Config.cards_api}/card-transactions`, params, null, true, true);
    },

    /**
     * Create bill
     */
    createBill({
        beneficiaryId,
        totalAmount,
        currency,
        dueDate,
        billReference,
        source,
        attachmentLink,
        billSellCurrency,
        rate,
        accountingBankFeedOnly,
        accountingInvoiceDate,
        accountingGlCode,
        accountingTaxCode,
        description,
    }) {
        const url = `${Config.bills_api_v1}`;
        return postWithoutOverride(
            url,
            {
                totalAmount: Number(totalAmount),
                currency,
                dueDate,
                beneficiaryId,
                instalments: [],
                billReference,
                source,
                billSellCurrency,
                ...(attachmentLink && { attachmentLink }),
                ...(rate && { rate }),
                accountingBankFeedOnly,
                accountingInvoiceDate,
                accountingGlCode,
                accountingTaxCode,
                description,
            },
            null,
            true
        );
    },

    addPaymentsToBill({ billId, paymentIds }) {
        const url = `${Config.bills_api_v1}/${billId}/payment`;
        return putWithoutOverride(
            url,
            {
                paymentIds,
            },
            null,
            true
        );
    },

    removePaymentFromBill({ billId, paymentId }) {
        return httpDelete(
            `${Config.bills_api_v1}/${billId}/payment`,
            {
                paymentId,
            },
            null,
            true
        );
    },

    getBill({ billId }) {
        const url = `${Config.bills_api_v1}/${billId}`;
        return getWithoutOverride(url, null, '', true);
    },

    getBills() {
        const url = `${Config.bills_api_v1}/`;
        return getWithoutOverride(url, null, '', true);
    },

    deleteBill({ billId }) {
        return httpDelete(`${Config.bills_api_v1}/${billId}`, {}, null, true);
    },

    createQuote(params) {
        return createQuote(params);
    },

    /** search card transactions
     *
     * @param { cardId: string, companyId: string,  index: number, searchValue: string } params
     */
    searchCardTransactions(params) {
        return getWithoutOverride(`${Config.cards_api}/analytics/card-transactions`, params, null, true, true);
    },

    /** get card statement pdf
     *
     * @param { startDate: string, endDate: string, cardId?: string } params
     */
    getCardStatement(params) {
        return getWithoutOverride(`${Config.cards_api}/statements`, params, null, true, true);
    },

    getBatchFile(fileId) {
        return getWithoutOverride(`${Config.bulk_payments_api}/${fileId}`, null, null, true);
    },

    getBatchFileRows(fileId) {
        return getWithoutOverride(`${Config.bulk_payments_api}/${fileId}/rows`, null, null, true);
    },

    /**
     * Search recent deposits
     */
    searchRecentDeposits() {
        return getWithoutOverride(`${Config.cards_api}/analytics/recent-deposits`, {}, null, true, true);
    },

    /**
     * Get top 5 suppliers
     */
    getTopFiveSuppliers({ startDate, endDate }) {
        const params = {
            startDate,
            endDate,
        };
        return getWithoutOverride(`${Config.cards_api}/analytics/top-five-suppliers`, params, null, true, true);
    },

    getDepositsTimeseries(params) {
        return getWithoutOverride(`${Config.cards_api}/analytics/deposits-timeseries`, params, null, true, true);
    },

    getOutflowTransactionsTimeseries(params) {
        return getWithoutOverride(
            `${Config.cards_api}/analytics/outflow-transactions-timeseries`,
            params,
            null,
            true,
            true
        );
    },

    getPaymentCounts() {
        return getWithoutOverride(`${Config.cards_api}/analytics/payments`, {}, null, true, true);
    },

    /**
     * { startDate, endDate }
     */
    getPaymentsByCurrency(params, companyId = null) {
        return getWithoutOverride(
            `${Config.cards_api}/analytics/payment-currencies`,
            {
                companyId: companyId || getCurrentCompanyId(),
                ...params,
            },
            null,
            true,
            true
        );
    },

    findDuplicatePayments({ invoiceNumber, beneficiaryName }) {
        return getWithoutOverride(
            `${Config.cards_api}/analytics/duplicate-payments`,
            { invoiceNumber, beneficiaryName },
            null,
            true,
            true
        );
    },

    getRecentPayments(size, statuses, after) {
        return getWithoutOverride(
            `${Config.payments2_api}?page[size]=${size}&filter[status]=${encodeURIComponent(
                statuses.map((status, index) => `${status}${statuses.length - 1 !== index ? '+' : ''}`).join('')
            )}${after ? `&page[after]=${after}` : ''}`,
            null,
            null,
            true
        );
    },

    getPayment(paymentId) {
        return getWithoutOverride(`${Config.payments2_api}/${paymentId}`, null, null, true);
    },

    /**
     *  submitted filter to get OcrOnlySubmitted transactions
     *  NOTE: Currently not used anywhere
     */
    getDraftPayments(size, after, submitted) {
        return getWithoutOverride(
            `${Config.payments2_api}/draft?page[size]=${size}${submitted ? `&filter[submitted]=${submitted}` : ''}` +
                `${after ? `&page[after]=${after}` : ''}`,
            null,
            null,
            true
        );
    },

    // NOTE: Looks like this is only used in function updatePaymentDateToToday
    /**
     *  https://apidocs.paytron.com/reference/update-payment
     *  @param paymentId : string
     *  @param body : { quoteId: string, reference: string, reason: string, purposeCode: string, paymentDate: string, status: ["DRAFT", "NEEDS APPROVAL", "APPROVED"] }
     */
    updatePayment(paymentId, { quoteId, reference, reason, purposeCode, paymentDate, status }) {
        return patchWithoutOverride(
            `${Config.payments2_api}/${paymentId}`,
            {
                data: {
                    type: 'payments',
                    attributes: {
                        reference,
                        reason,
                        purposeCode,
                        paymentDate,
                        status,
                    },
                    relationships: {
                        quote: {
                            data: {
                                id: quoteId,
                                type: 'quote',
                            },
                        },
                    },
                },
            },
            null,
            true,
            false
        );
    },

    updateDraftPayment(paymentId, params) {
        return patchWithoutOverride(`${Config.payments2_api}/drafts/${paymentId}`, params, null, true, true);
    },

    /**
     *  https://apidocs.paytron.com/reference/batch-update-payments
     *  @param payments: [{"paymentId": string, status: "DRAFT", paymentDate: "2022-08-23", reference: string}]
     */
    batchUpdatePayments(payments) {
        return patchWithoutOverride(`${Config.payments2_api}/batch-drafts`, payments, null, true, true);
    },

    // NOTE: This endpoint is currently not used anywhere
    /**
     * https://apidocs.paytron.com/reference/batch-delete-payments
     *  @param paymentIds: [{paymentId: "123"}, {paymentId: "123"}]
     */
    batchDeletePayments(paymentIds) {
        return httpDelete(`${Config.payments2_api}/batch`, paymentIds, null, true, true);
    },

    getPurposeCodes(country, currency, entityType) {
        return getWithoutOverride(
            `${Config.payments2_api}/purpose-codes?country=${country}&currency=${currency}&entityType=${entityType}`,
            {},
            null,
            true,
            true
        );
    },

    /**
     * Method for creating multiple custom categories at once
     * @params
     * ```
     * {
     *  categories: [
     *      { name: string, parentId?: string }
     *  ]
     * }
     * ```
     */
    addCustomCategories(params) {
        return postWithoutOverride(Config.custom_categories_api, params, null, true);
    },
    /**
     * Method for creating multiple custom category groups at once
     * @params
     * ```
     * {
     *  categories: [
     *      [{parent: string, mandatory?: boolean, defaultId?: boolean, children: [{ id: string, name: string }]}]
     *  ]
     * }
     * ```
     */

    /**
     * Method for editing multiple custom categories at once
     * @params
     * ```
     * {
     *  categories: [
     *      { name: string, parentId?: string, mandatory?: boolean, defaultId?: string, status?: string }
     *  ]
     * }
     * ```
     */
    editCustomCategories(params) {
        return putWithoutOverride(Config.custom_categories_api, params, null, true);
    },
    createCategoryGroups(params) {
        return postWithoutOverride(`${Config.custom_categories_api}/groups`, params, null, true);
    },
    getParentCategories() {
        return getWithoutOverride(Config.custom_categories_api, {}, null, true);
    },
    getChildCategories(id) {
        return getWithoutOverride(`${Config.custom_categories_api}/${id}/categories`, {}, null, true);
    },
    getParentCategory(id) {
        return getWithoutOverride(`${Config.custom_categories_api}/${id}`, {}, null, true);
    },
    getExpenseCategory() {
        return getWithoutOverride(`${Config.custom_categories_api}/expenseCategory`, {}, null, true);
    },
    getChildCategory(childId, parentId) {
        return getWithoutOverride(`${Config.custom_categories_api}/${parentId}/categories/${childId}`, {}, null, true);
    },

    /**
     * Gets currency rates
     * @param {currency: string} params
     */
    getCurrencyRates(params) {
        return getWithoutOverride(`${Config.cards_api}/currency-rates`, params, null, true, true);
    },

    combinePayments(paymentIds, beneficiaryId) {
        return postWithoutOverride(
            `${Config.groups_json_api_v1}`,
            {
                payments: paymentIds,
                beneficiaryId,
            },
            null,
            true
        );
    },

    getGroup(groupId) {
        return getWithoutOverride(`${Config.groups_json_api_v1}/${groupId}`, {}, null, true);
    },

    getFrankieSession(params) {
        return postWithoutOverride(`${Config.frankieone_api}/machineSession`, params, null, true, true);
    },
    notifyFrankieComplete(params) {
        return postWithoutOverride(`${Config.frankieone_api}/kycComplete`, params, null, true, true);
    },
    notifyFrankieCompletePublic(params) {
        return postWithoutOverride(`${Config.frankieone_api}/kycCompletePublic`, params, null);
    },
    getPublicFrankieSession(params) {
        return postWithoutOverride(`${Config.frankieone_api}/machineSessionPublic`, params);
    },

    searchRolledUpTransactions({ filters, sortBy, sortByType, search, index, baseCurrency }) {
        let params = '';
        if (filters) {
            params += `&filters=${encodeURIComponent(filters)}`;
        }
        if (sortBy) {
            params += `&sortBy=${encodeURIComponent(sortBy)}`;
        }
        if (sortByType) {
            params += `&sortByType=${encodeURIComponent(sortByType)}`;
        }
        if (search) {
            params += `&search=${encodeURIComponent(search)}`;
        }
        if (index) {
            params += `&index=${encodeURIComponent(index)}`;
        }
        if (baseCurrency) {
            params += `&baseCurrency=${baseCurrency}`;
        }

        return getWithoutOverride(
            `${Config.cards_api}/analytics/rolled-up-transactions/search?${params}`,
            {},
            null,
            true,
            true
        );
    },

    /**
     * Approval Polices
     */

    /*
    Policy example
    {
        conditions: [
            {
                attribute: 'amount',
                operator: 'greater_than',
                value: '1000',
            },
        ],
        stages: [
            {
                minimumRequired: 1,
                approvers: [
                    {
                        id: '123',
                        type: 'user',
                    },
                ],
            },
        ],
        resourceType: 'expenses',
        };
   */
    createApprovalPolicy({ name, resourceType, conditions, stages }) {
        const params = {
            name,
            resourceType,
            conditions,
            stages,
        };
        return postWithoutOverride<Partial<{ id: string; message: string }>>( // defensively typed response based on existing usage
            `${Config.approvals_api}/policies`,
            params,
            null,
            true,
            true
        );
    },
    updateApprovalPolicy({ name, resourceType, conditions, stages, id }) {
        const params = {
            name,
            conditions,
            stages,
            active: 'ENABLED',
        };
        return putWithoutOverride(`${Config.approvals_api}/policies/${resourceType}/${id}`, params, null, true, true);
    },
    listApprovalPolicies({ resourceType, cursor = 0 }: { resourceType: string; cursor?: number }) {
        return getWithoutOverride<{ data: Policy[]; cursor: number }>(
            `${Config.approvals_api}/policies/${resourceType}`,
            { cursor },
            null,
            true,
            true
        );
    },
    getApprovalPolicy({ resourceType, id }) {
        return getWithoutOverride(`${Config.approvals_api}/policies/${resourceType}/${id}`, {}, null, true, true);
    },
    getApprovalRecord({ resourceType, id }) {
        return getWithoutOverride(
            `${Config.approvals_api}/approval-records/${resourceType}/${id}`,
            {},
            null,
            true,
            true
        );
    },
    getApprovalRecords({
        cursor,
        userStatus,
        limit,
    }: { cursor?: string; userStatus?: 'approved' | 'rejected'; limit?: string } = {}) {
        return getWithoutOverride(
            `${Config.approvals_api}/approval-records`,
            { ...(userStatus && { userStatus }), ...(cursor && { cursor }), limit: limit || '50' },
            null,
            true,
            true
        );
    },
    /**
     *
     * @param {'payment' | 'expense'} resourceType
     * @param {string} id
     * @param {'approved' | 'rejected'} status
     * @returns
     */
    actionRecord({ resourceType, resourceId, status }) {
        return patchWithoutOverride(
            `${Config.approvals_api}/approval-records/${resourceType}/${resourceId}`,
            { status, type: 'user' },
            null,
            true,
            true
        );
    },

    /**
     *
     * @param {'team' | 'department' | 'company' | 'user'} type
     * @param {Array[{ status: 'approved' | 'rejected'; resourceType: 'payment' | 'expense'; resourceId: string }]} approvals
     * @returns
     */
    actionRecords({ type = 'user', approvals }) {
        return patchWithoutOverride(`${Config.approvals_api}/approval-records`, { type, approvals }, null, true, true);
    },

    /**
     * Updates the list of priorites, how the policies are ordered. Any IDs omitted from priorities array will be hidden from FE
     * @param {string[]} priorities
     * @param {string} resourceType
     * @returns
     */
    updateApprovalPolicyPriorities(priorities, resourceType) {
        const params = {
            priorities,
        };
        return patchWithoutOverride(`${Config.approvals_api}/priorities/${resourceType}`, params, null, true, true);
    },

    /**
     * Deletes approval records. This will also move a the resource back into draft by publishing a
     * RESOURCE_DELETED event into the approval bus; which other services pick up.
     * The status updates are currently implemented for payments and expenses
     * @param {{id: string, resourceType: string}[]} approvalRecords
     * @returns
     */
    deleteApprovalRecords(approvalRecords) {
        return httpDelete(`${Config.approvals_api}/approval-records`, { approvalRecords }, null, true, true);
    },

    // AUDITS
    getAudit({ resourceId, startKey }) {
        return getWithoutOverride(
            `${Config.audits_api}/audit/${resourceId}?&pageLimit=10${
                startKey ? `&startKey=${encodeURIComponent(startKey)}` : ''
            }`,
            {},
            null,
            true,
            true
        );
    },

    // NOTIFICATIONS
    /**
     * Get a given push notification token, registers it with Pusher Beams and adds it to the user record
     * @param {{token: string, platform: 'android' | 'ios'}}
     * @returns
     */
    addDeviceForNotifications({ token, platform }) {
        const params = { token, platform };
        return postWithoutOverride(`${Config.notifications_api}/add-device`, params);
    },

    listRawPayments({ nextPage, filter, orderBy, pageSize, companyId } = {}) {
        const url = new URL(`${Config.payments2_api}/raw`);

        if (!nextPage) {
            if (filter) {
                url.searchParams.append('filter[status]', filter);
            }

            if (orderBy) {
                url.searchParams.append('orderBy', orderBy);
            }

            if (pageSize) {
                url.searchParams.append('page[size]', pageSize);
            }
        }

        return getWithoutOverride(nextPage ? `${url}?${nextPage}` : url, null, null, true, true, {
            ...(companyId && {
                headers: {
                    'X-Paytron-Account': companyId,
                },
            }),
        });
    },

    searchPayments(params) {
        return getWithoutOverride(
            `${Config.cards_api}/analytics/payment-text-search`,
            {
                companyId: getCurrentCompanyId(),
                ...params,
            },
            null,
            true
        );
    },

    getTotalStatusCurrency(params: { companyId?: string; payDateEnd?: string } | undefined) {
        return getWithoutOverride(
            `${Config.cards_api}/analytics/payment-total-status-currency`,
            {
                ...(params?.payDateEnd && { payDateEnd: params?.payDateEnd }),
            },
            null,
            true,
            true,
            {
                ...(params?.companyId && { headers: { 'X-Paytron-Account': params.companyId } }),
            }
        );
    },
};

const xeroApiMethods = ({ taxRates, setTaxRates }: { taxRates: undefined; setTaxRates }) => ({
    getXeroAccounts() {
        return getWithoutOverride(`${Config.api}/xero/accounts`, null, false, true);
    },
    getXeroTrackingCategories() {
        return getWithoutOverride(`${Config.api}/xero/categories`, null, false, true);
    },
    getXeroTaxRates() {
        if (taxRates) {
            console.log('getting cache');
            return taxRates;
        }
        console.log('getting rates');
        const response = getWithoutOverride(`${Config.api}/xero/tax-rates`, null, false, true);
        setTaxRates(response);
        return response;
    },
    syncXeroPayment({ paymentId }) {
        return postWithoutOverride(`${Config.api}/xero/payments/sync/${paymentId}`, undefined, undefined, true);
    },
});

export const BackendAccessProvider = ({ children }) => {
    const [prism, setprism] = useState<{ [key: string]: PrismItem }>({});
    const [isPrismLoading, setIsPrismLoading] = useState(true);
    const [trackingCategoryData, setTrackingCategoryData] = useState([]);
    const [taxRates, setTaxRates] = useState(undefined);

    useEffect(() => {
        const getFeatures = async () => {
            try {
                const response = await API.getPrismFeatures();
                console.log('Enabled features:', response.data);
                setprism(response.data);
            } catch (err) {
                console.error(err);
            } finally {
                setIsPrismLoading(false);
            }
        };
        getFeatures();
    }, []);

    return (
        <BackendAccessContext.Provider
            value={{
                ...API,
                ...xeroApiMethods({ taxRates, setTaxRates }),
                prism,
                isPrismLoading,
                trackingCategoryData,
                setTrackingCategoryData,
            }}
        >
            {children}
        </BackendAccessContext.Provider>
    );
};
